/**
 * What a value changed in Priorities.ai looks like in a linked system: which of
 * its fields it goes to, and in which form. Used by the `dispatch` function to
 * send values out under the Field Sync Rules (./fieldSyncRules.ts).
 *
 * Plain item fields go to fixed fields per system. A scoring attribute (Impact,
 * Effort, …) goes where the connector's Field Mappings rule for it points, its
 * value read backwards through the rule's value list ("9" → the label
 * "Impact: 9"). Pure: no I/O.
 */

/** The Item fields each system takes, by our key → its field. */
export const OUTBOUND_ITEM_FIELDS: Record<string, Record<string, string>> = {
  jira: { title: 'summary', description: 'description', tags: 'labels' },
  linear: { title: 'title', description: 'description' },
};

/** The fields of each system the engine can write (`/jira/items/update`, `/linear/issues/update`). */
export const ENGINE_WRITABLE_FIELDS: Record<string, ReadonlySet<string>> = {
  jira: new Set(['summary', 'description', 'labels', 'story_points']),
  linear: new Set(['title', 'description', 'labels', 'estimate']),
};

export interface StoredMappingEntry {
  id?: string;
  externalFieldKey?: string;
  internalFieldKey?: string;
  direction?: string;
  enabled?: boolean;
  valueMap?: Array<{ externalValue: string; internalValue: string }>;
  scaleId?: string;
}

interface StoredMappingConfig {
  sets?: Array<{ id?: string; entries?: StoredMappingEntry[] }>;
  importSetId?: string | null;
  exportSetId?: string | null;
}

/**
 * The connector's Field Mappings rule for one Item field: in the set chosen for
 * export, else the set chosen for import, else any set. Null when none names it.
 */
// reuse-ack: mirrors src/lib/fieldMappingApply.ts resolveActiveMappingSet — an edge function cannot import from src/; this reads only the stored JSON.
export function mappingEntryFor(fieldMappings: unknown, internalFieldKey: string): StoredMappingEntry | null {
  const config = (fieldMappings && typeof fieldMappings === 'object' && !Array.isArray(fieldMappings)
    ? fieldMappings
    : {}) as StoredMappingConfig;
  const sets = config.sets ?? [];
  const ordered = [
    ...sets.filter((s) => s.id && s.id === config.exportSetId),
    ...sets.filter((s) => s.id && s.id === config.importSetId && s.id !== config.exportSetId),
    ...sets.filter((s) => !s.id || (s.id !== config.exportSetId && s.id !== config.importSetId)),
  ];
  for (const set of ordered) {
    const hit = (set.entries ?? []).find(
      (e) => e.enabled !== false && e.internalFieldKey === internalFieldKey && !!e.externalFieldKey,
    );
    if (hit) return hit;
  }
  return null;
}

/** Same value however it was stored: '8' and 8 match. */
export function sameStoredValue(a: unknown, b: unknown): boolean {
  if (String(a ?? '').trim() === String(b ?? '').trim()) return true;
  const x = Number(a);
  const y = Number(b);
  return String(a ?? '').trim() !== '' && String(b ?? '').trim() !== '' && Number.isFinite(x) && Number.isFinite(y) && x === y;
}

export type OutboundValue =
  | {
      ok: true;
      /** The other system's field. */
      field: string;
      value: unknown;
      /** For a label field: the other labels of the same value list, taken off when this one goes on. */
      replaceLabels: string[];
    }
  | { ok: false; reason: string };

/** The value of one Item field, in the form one linked system takes it. */
export function outboundValueFor(
  connectorType: string,
  internalFieldKey: string,
  value: unknown,
  fieldMappings: unknown,
  systemName: string,
  /** For an attribute: the scale the value is on. A rule that names another scale does not send it. */
  scaleId?: string | null,
): OutboundValue {
  const writable = ENGINE_WRITABLE_FIELDS[connectorType];
  if (!writable) return { ok: false, reason: `sending values to ${systemName} is not built` };

  const entry = mappingEntryFor(fieldMappings, internalFieldKey);
  const field = entry?.externalFieldKey ?? OUTBOUND_ITEM_FIELDS[connectorType]?.[internalFieldKey];
  if (!field) return { ok: false, reason: `no Field Mappings rule says which ${systemName} field this is` };
  if (!writable.has(field)) return { ok: false, reason: `${systemName}'s “${field}” cannot be written yet` };
  if (entry?.scaleId && scaleId !== undefined && (scaleId ?? null) !== entry.scaleId) {
    return { ok: false, reason: `the ${systemName} rule sends this value on another scale` };
  }

  if (entry?.valueMap?.length) {
    const hit = entry.valueMap.find((m) => sameStoredValue(m.internalValue, value));
    if (!hit) return { ok: false, reason: `the value list has no ${systemName} value for “${String(value)}”` };
    const replaceLabels = field === 'labels' ? entry.valueMap.map((m) => m.externalValue).filter((v) => v !== hit.externalValue) : [];
    return { ok: true, field, value: hit.externalValue, replaceLabels };
  }
  return { ok: true, field, value, replaceLabels: [] };
}
